import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Benutzer } from '../types';
import BenutzerForm, { EinladungsErgebnis, registrierLink } from './BenutzerForm';
import './BenutzerList.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

const ROLLE_FARBE: Record<string, { bg: string; color: string }> = {
  'Admin':              { bg: 'var(--bone-2)', color: 'var(--red)' },
  'Geschäftsführer':   { bg: 'var(--bone-2)', color: 'var(--green-900)' },
  'Produktionsleiter': { bg: 'var(--bone-2)', color: 'var(--green-500)' },
  'Lagerleiter':       { bg: 'var(--bone-2)', color: 'var(--gold)' },
  'Einkäufer':         { bg: 'var(--bone-2)', color: 'var(--green-900)' },
  'Verkäufer':         { bg: 'var(--bone-2)', color: 'var(--green-500)' },
  'Buchhaltung':       { bg: 'var(--bone-2)', color: 'var(--ink-2)' },
};

interface Props {
  authToken: string;
}

const BenutzerList: React.FC<Props> = ({ authToken }) => {
  const [benutzer, setBenutzer] = useState<Benutzer[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingBenutzer, setEditingBenutzer] = useState<Benutzer | null>(null);
  const [filterText, setFilterText] = useState('');
  const [filterRolle, setFilterRolle] = useState('');
  const [filterAktiv, setFilterAktiv] = useState<'alle' | 'aktiv' | 'inaktiv'>('aktiv');
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [pflicht, setPflicht] = useState<boolean | null>(null);
  const [emailMoeglich, setEmailMoeglich] = useState(true);
  const [ergebnis, setErgebnis] = useState<EinladungsErgebnis | null>(null);
  const [linkSichtbar, setLinkSichtbar] = useState(false);

  const headers = useMemo(() => ({ Authorization: `Bearer ${authToken}` }), [authToken]);

  const loadBenutzer = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/benutzer`, { headers });
      setBenutzer(res.data);
    } catch (err) {
      console.error('Fehler beim Laden der Benutzer', err);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { loadBenutzer(); }, [loadBenutzer]);

  const loadPflicht = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/benutzer/zwei-faktor-pflicht`, { headers });
      // null = fuer dieses Konto nicht einstellbar (Admin ohne Mandant) -> Schalter ausblenden
      const wert = r.data.pflicht;
      setPflicht(wert === null || wert === undefined ? null : !!wert);
      setEmailMoeglich(!!r.data.email_versand_moeglich);
    } catch {
      setPflicht(null);   // z.B. fehlende Berechtigung → Schalter ausblenden
    }
  }, [headers]);

  useEffect(() => { loadPflicht(); }, [loadPflicht]);

  const pflichtSetzen = async (wert: boolean) => {
    try {
      const r = await axios.put(`${API_URL}/api/benutzer/zwei-faktor-pflicht`, { pflicht: wert }, { headers });
      setPflicht(!!r.data.pflicht);
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Einstellung konnte nicht gespeichert werden');
    }
  };

  const einladungSenden = async (b: Benutzer) => {
    setActionLoading(b.benutzer_id);
    try {
      const r = await axios.post(`${API_URL}/api/benutzer/${b.benutzer_id}/einladung`, {}, { headers });
      setErgebnis({ email: b.email, link: registrierLink(r.data.einladungs_token), email_versendet: !!r.data.email_versendet });
      setLinkSichtbar(false);
      await loadBenutzer();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Einladung konnte nicht erzeugt werden');
    } finally {
      setActionLoading(null);
    }
  };

  const zweiFaktorReset = async (b: Benutzer) => {
    if (!window.confirm(`Zwei-Faktor für ${b.name} zurücksetzen? Die Person muss 2FA danach neu einrichten.`)) return;
    setActionLoading(b.benutzer_id);
    try {
      await axios.post(`${API_URL}/api/benutzer/${b.benutzer_id}/zwei-faktor-zuruecksetzen`, {}, { headers });
      await loadBenutzer();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Zurücksetzen fehlgeschlagen');
    } finally {
      setActionLoading(null);
    }
  };

  const handleEdit = (b: Benutzer) => {
    setEditingBenutzer(b);
    setShowForm(true);
  };

  const handleNeu = () => {
    setEditingBenutzer(null);
    setShowForm(true);
  };

  const handleSuccess = () => {
    setShowForm(false);
    setEditingBenutzer(null);
    loadBenutzer();
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingBenutzer(null);
  };

  const handleToggleAktiv = async (b: Benutzer) => {
    setActionLoading(b.benutzer_id);
    try {
      const action = b.aktiv ? 'deaktivieren' : 'aktivieren';
      await axios.put(`${API_URL}/api/benutzer/${b.benutzer_id}/${action}`, {}, { headers });
      await loadBenutzer();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Fehler beim Ändern des Status');
    } finally {
      setActionLoading(null);
    }
  };

  if (showForm) {
    return (
      <BenutzerForm
        editingBenutzer={editingBenutzer}
        authToken={authToken}
        onSuccess={handleSuccess}
        onCancel={handleCancel}
        onEinladung={(e) => { setShowForm(false); setEditingBenutzer(null); setErgebnis(e); setLinkSichtbar(false); loadBenutzer(); }}
      />
    );
  }

  const gefiltert = benutzer.filter(b => {
    const textMatch = !filterText ||
      b.name.toLowerCase().includes(filterText.toLowerCase()) ||
      b.email.toLowerCase().includes(filterText.toLowerCase());
    const rolleMatch = !filterRolle || (b.rollen ?? [b.rolle]).includes(filterRolle);
    const aktivMatch =
      filterAktiv === 'alle' ||
      (filterAktiv === 'aktiv' && b.aktiv) ||
      (filterAktiv === 'inaktiv' && !b.aktiv);
    return textMatch && rolleMatch && aktivMatch;
  });

  const alleRollen = Array.from(new Set(benutzer.flatMap(b => b.rollen ?? [b.rolle]))).sort();

  return (
    <div className="benutzer-list-container">
      <div className="benutzer-list-header">
        <div>
          <h2 className="benutzer-list-title">Benutzerverwaltung</h2>
          <p className="benutzer-list-subtitle">{benutzer.filter(b => b.aktiv).length} aktive Benutzer</p>
        </div>
        <button className="btn-primary" onClick={handleNeu}>
          + Neuer Benutzer
        </button>
      </div>

      {ergebnis && (
        <div className="benutzer-einladung-ergebnis">
          {ergebnis.email_versendet ? (
            <p>Einladung an <strong>{ergebnis.email}</strong> verschickt. Der Link ist 72 Stunden gültig.</p>
          ) : (
            <p>Mailversand ist nicht eingerichtet – bitte den Link manuell an <strong>{ergebnis.email}</strong> weitergeben.</p>
          )}
          {(!ergebnis.email_versendet || linkSichtbar) ? (
            <input type="text" readOnly value={ergebnis.link} onFocus={e => e.target.select()} style={{ width: '100%' }} />
          ) : (
            <button className="btn-edit" type="button" onClick={() => setLinkSichtbar(true)}>Link trotzdem anzeigen</button>
          )}
          <button className="btn-edit" type="button" onClick={() => setErgebnis(null)}>Schließen</button>
        </div>
      )}

      {pflicht !== null && (
        <div className="benutzer-pflicht-row">
          <label>
            <input type="checkbox" checked={pflicht} onChange={e => pflichtSetzen(e.target.checked)} />
            {' '}Zwei-Faktor-Pflicht für alle Benutzer
          </label>
          <span className="benutzer-list-subtitle">Nutzer ohne 2FA müssen sie beim nächsten Login einrichten.</span>
          {!emailMoeglich && <span className="benutzer-list-subtitle"> Hinweis: Mailversand ist nicht eingerichtet, E-Mail-Codes sind dann nicht möglich.</span>}
        </div>
      )}

      <div className="benutzer-filter-row">
        <input
          type="text"
          placeholder="Name oder E-Mail suchen..."
          value={filterText}
          onChange={e => setFilterText(e.target.value)}
          className="benutzer-search-input"
        />
        <select
          value={filterRolle}
          onChange={e => setFilterRolle(e.target.value)}
          className="benutzer-filter-select"
        >
          <option value="">Alle Rollen</option>
          {alleRollen.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <div className="benutzer-aktiv-toggle">
          {(['aktiv', 'inaktiv', 'alle'] as const).map(opt => (
            <button
              key={opt}
              className={`toggle-btn ${filterAktiv === opt ? 'active' : ''}`}
              onClick={() => setFilterAktiv(opt)}
            >
              {opt.charAt(0).toUpperCase() + opt.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="benutzer-loading">Benutzer werden geladen...</div>
      ) : gefiltert.length === 0 ? (
        <div className="benutzer-empty">Keine Benutzer gefunden.</div>
      ) : (
        <table className="benutzer-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>E-Mail</th>
              <th>Rolle</th>
              <th>Status</th>
              <th>2FA</th>
              <th>Erstellt</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {gefiltert.map(b => {
              return (
                <tr key={b.benutzer_id} className={!b.aktiv ? 'row-inaktiv' : ''}>
                  <td className="benutzer-name">{b.name}</td>
                  <td className="benutzer-email">{b.email}</td>
                  <td>
                    {(b.rollen && b.rollen.length > 0 ? b.rollen : [b.rolle]).map(r => {
                      const f = ROLLE_FARBE[r] || { bg: 'var(--bone-2)', color: 'var(--ink)' };
                      return (
                        <span
                          key={r}
                          className="rolle-badge"
                          style={{ background: f.bg, color: f.color, marginRight: 4 }}
                        >
                          {r}
                        </span>
                      );
                    })}
                  </td>
                  <td>
                    <span className={`status-badge ${b.aktiv ? 'aktiv' : 'inaktiv'}`}>
                      {b.einladung_offen ? 'Eingeladen' : b.aktiv ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </td>
                  <td>{b.zwei_faktor_methode === 'totp' ? 'App' : b.zwei_faktor_methode === 'email' ? 'E-Mail' : '—'}</td>
                  <td className="benutzer-datum">
                    {new Date(b.erstellt_am).toLocaleDateString('de-DE')}
                  </td>
                  <td className="benutzer-actions">
                    <button className="btn-edit" onClick={() => handleEdit(b)}>
                      Bearbeiten
                    </button>
                    <button
                      className={`btn-toggle ${b.aktiv ? 'deaktivieren' : 'aktivieren'}`}
                      onClick={() => handleToggleAktiv(b)}
                      disabled={actionLoading === b.benutzer_id}
                    >
                      {actionLoading === b.benutzer_id
                        ? '...'
                        : b.aktiv ? 'Deaktivieren' : 'Aktivieren'}
                    </button>
                    <button className="btn-edit" onClick={() => einladungSenden(b)} disabled={actionLoading === b.benutzer_id}
                            title="Einladungs- bzw. Passwort-Reset-Link per E-Mail senden">
                      {b.einladung_offen ? 'Einladung erneut senden' : 'Reset-Link senden'}
                    </button>
                    {b.zwei_faktor_methode && (
                      <button className="btn-edit" onClick={() => zweiFaktorReset(b)} disabled={actionLoading === b.benutzer_id}>
                        2FA zurücksetzen
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default BenutzerList;
